import React from 'react';
import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { FmButton } from './FmButton';
import { FmChip } from './FmChip';
import { FmTabsList, FmTabsPanel, FmTabsRoot, FmTabsTrigger } from './FmTabs';
import './FmCapabilityShowcase.css';

export interface FmCapabilityShowcaseItem {
  key: string;
  /** Label rendered in the vertical selector list. */
  label: React.ReactNode;
  /** Description shown when the item is the active capability. */
  description: React.ReactNode;
  /** Optional feature chips shown alongside the description when active. */
  tags?: React.ReactNode[];
  statusLabel?: React.ReactNode;
  disabledReason?: string;
  /** Optional call-to-action rendered when active; routes to the capability. */
  cta?: { label: React.ReactNode; href?: string; onClick?: () => void };
  /** Media/visual for this capability (image, chart, placeholder). */
  media?: React.ReactNode;
  /**
   * Optional decorative icon rendered inside the DS-owned, tokenized media
   * placeholder frame when no full `media` node is supplied. Lets callers pass
   * just an icon glyph without recreating the framed placeholder geometry in
   * page CSS/inline styles.
   */
  mediaIcon?: React.ReactNode;
  'data-testid'?: string;
}

export interface FmCapabilityShowcaseProps {
  items: FmCapabilityShowcaseItem[];
  /** Controlled active key. */
  activeKey?: string;
  /** Uncontrolled initial active key (defaults to items[0].key). */
  defaultActiveKey?: string;
  onActiveChange?: (key: string) => void;
  /** Auto-cycle interval in ms; 0/undefined disables auto-cycle. Default 6000. */
  autoCycleMs?: number;
  /** Media column side. Default 'left'. */
  side?: 'left' | 'right';
  eyebrow?: React.ReactNode;
  title?: React.ReactNode;
  /** Visual tone. Default 'light'. */
  tone?: 'light' | 'dark';
  /**
   * @internal DS-internal className prop; forbidden in route-owned code.
   */
  className?: string;
  'data-testid'?: string;
}

const cx = (...parts: Array<string | false | null | undefined>): string =>
  parts.filter(Boolean).join(' ');

function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return false;
  }
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/** Resolve the active key against the item set, falling back to the first item. */
function resolveActiveKey(
  items: FmCapabilityShowcaseItem[],
  candidate: string | undefined,
): string {
  if (candidate && items.some((item) => item.key === candidate)) {
    return candidate;
  }
  return items[0]?.key ?? '';
}

function hasCapabilityMedia(items: FmCapabilityShowcaseItem[]): boolean {
  return items.some((item) => Boolean(item.media || item.mediaIcon));
}

/** Controlled/uncontrolled active-key state with a stable setter. */
function useActiveKey(args: {
  items: FmCapabilityShowcaseItem[];
  activeKey?: string;
  defaultActiveKey?: string;
  onActiveChange?: (key: string) => void;
}): [string, (key: string) => void] {
  const { items, activeKey, defaultActiveKey, onActiveChange } = args;
  const isControlled = activeKey !== undefined;
  const [uncontrolled, setUncontrolled] = useState(() => resolveActiveKey(items, defaultActiveKey));
  const resolved = resolveActiveKey(items, isControlled ? activeKey : uncontrolled);

  const setActive = useCallback(
    (key: string) => {
      if (!isControlled) {
        setUncontrolled(key);
      }
      onActiveChange?.(key);
    },
    [isControlled, onActiveChange],
  );

  return [resolved, setActive];
}

/**
 * Auto-cycle the active item on an interval. Pauses while `paused` is true
 * (hover/focus), resets the timer when `resetToken` changes (user selection),
 * and is fully disabled under `prefers-reduced-motion: reduce`.
 */
function useAutoCycle(args: {
  autoCycleMs: number | undefined;
  paused: boolean;
  resetToken: string;
  onAdvance: () => void;
}): void {
  const { autoCycleMs, paused, resetToken, onAdvance } = args;
  const advanceRef = useRef(onAdvance);
  advanceRef.current = onAdvance;

  useEffect(() => {
    if (!autoCycleMs || autoCycleMs <= 0 || paused || prefersReducedMotion()) {
      return undefined;
    }
    const id = window.setInterval(() => advanceRef.current(), autoCycleMs);
    return () => window.clearInterval(id);
  }, [autoCycleMs, paused, resetToken]);
}

interface CapabilityListProps {
  items: FmCapabilityShowcaseItem[];
  activeKey: string;
  labelId?: string;
  onSelect: (key: string) => void;
  /** Auto-cycle interval in ms; drives the progress bar under the active title. */
  autoCycleMs: number;
  /** Freeze the progress bar while the section is hovered/focused. */
  paused: boolean;
}

/**
 * A thin timer bar under the active title that fills over the auto-cycle
 * interval, so the user can see the section is actively counting to the next
 * capability. Restarts whenever the active item changes (keyed by activeKey);
 * freezes on hover/focus; hidden under prefers-reduced-motion (CSS).
 */
const CapabilityProgress: React.FC<{ durationMs: number; paused: boolean }> = ({
  durationMs,
  paused,
}) => (
  <div className="fm-capability-showcase__progress" aria-hidden="true">
    <span
      className={`fm-capability-showcase__progress-bar${paused ? ' fm-capability-showcase__progress-bar--paused' : ''}`}
      style={
        {
          '--fm-capability-progress-duration': `${durationMs}ms`,
        } as React.CSSProperties
      }
    />
  </div>
);

/**
 * Radix Tabs owns the tablist/tab/tabpanel wiring and the roving focus; the
 * vertical orientation matches the stacked list, so Up/Down move between items
 * and selection follows focus.
 */
const CapabilityList: React.FC<CapabilityListProps> = ({
  items,
  activeKey,
  labelId,
  onSelect,
  autoCycleMs,
  paused,
}) => (
  <FmTabsRoot
    value={activeKey}
    onValueChange={onSelect}
    orientation="vertical"
    activationMode="automatic"
  >
    <FmTabsList className="fm-capability-showcase__list" aria-labelledby={labelId}>
      {items.map((item) => {
        const isActive = item.key === activeKey;
        return (
          <div
            key={item.key}
            className={cx(
              'fm-capability-showcase__item-group',
              isActive && 'fm-capability-showcase__item-group--active',
            )}
          >
            <FmTabsTrigger
              value={item.key}
              className={cx(
                'fm-capability-showcase__item',
                isActive && 'fm-capability-showcase__item--active',
              )}
              data-testid={item['data-testid']}
            >
              <span className="fm-capability-showcase__item-label">{item.label}</span>
            </FmTabsTrigger>
            {/* Auto-cycle timer bar under the active title — keyed by the active
                 key so it restarts on every advance. Only when cycling is on. */}
            {isActive && autoCycleMs > 0 ? (
              <CapabilityProgress key={item.key} durationMs={autoCycleMs} paused={paused} />
            ) : null}
            {/* Description opens inline directly under its own title, not at the
                 bottom of the whole list, so the prose stays attached to the
                 capability it describes. */}
            <CapabilityPanel item={item} />
          </div>
        );
      })}
    </FmTabsList>
  </FmTabsRoot>
);

interface CapabilityPanelProps {
  item: FmCapabilityShowcaseItem;
}

/**
 * The item's `data-testid` names the TAB (which renders only the label). The
 * status label and CTA live in this panel, so the panel gets its own derived
 * `-panel` id — same convention as `-cta` and `-media` — giving callers a
 * stable handle for the active item's status/CTA content.
 */
const CapabilityPanel: React.FC<CapabilityPanelProps> = ({ item }) => (
  <FmTabsPanel
    value={item.key}
    className="fm-capability-showcase__panel"
    data-testid={item['data-testid'] ? `${item['data-testid']}-panel` : undefined}
  >
    <p className="fm-capability-showcase__description">{item.description}</p>
    {item.tags && item.tags.length > 0 ? (
      <div className="fm-capability-showcase__tags">
        {item.tags.map((tag, index) => (
          // Tags are arbitrary nodes with no stable id; the list is static per
          // render, so the index is a safe, stable key.
          // eslint-disable-next-line react/no-array-index-key
          <FmChip key={index} variant="mute">
            {tag}
          </FmChip>
        ))}
      </div>
    ) : null}
    {item.statusLabel ? <FmChip variant="mute">{item.statusLabel}</FmChip> : null}
    {item.cta ? (
      <div className="fm-capability-showcase__cta">
        {item.cta.href ? (
          <FmButton
            variant="primary"
            href={item.cta.href}
            onClick={item.cta.onClick}
            disabled={Boolean(item.disabledReason)}
            title={item.disabledReason}
            data-testid={item['data-testid'] ? `${item['data-testid']}-cta` : undefined}
          >
            {item.cta.label}
          </FmButton>
        ) : (
          <FmButton
            variant="primary"
            onClick={item.cta.onClick}
            disabled={Boolean(item.disabledReason)}
            title={item.disabledReason}
            data-testid={item['data-testid'] ? `${item['data-testid']}-cta` : undefined}
          >
            {item.cta.label}
          </FmButton>
        )}
      </div>
    ) : null}
  </FmTabsPanel>
);

function CapabilityMedia({
  media,
  mediaIcon,
  testId,
}: {
  media?: React.ReactNode;
  mediaIcon?: React.ReactNode;
  testId?: string;
}): React.ReactElement | null {
  if (!media && !mediaIcon) return null;
  return (
    <div className="fm-capability-showcase__media" aria-hidden="true" data-testid={testId}>
      {media ? (
        <div className="fm-capability-showcase__media-slot">{media}</div>
      ) : (
        <div className="fm-capability-showcase__media-placeholder">
          {mediaIcon ? (
            <span className="fm-capability-showcase__media-icon">{mediaIcon}</span>
          ) : null}
        </div>
      )}
    </div>
  );
}

/**
 * FmCapabilityShowcase — compact capability-showcase section.
 *
 * A media panel beside a vertical selector list of capabilities where exactly
 * one is active at a time. The active item expands to show its description,
 * optional feature chips, and a CTA that routes to the capability. The active
 * item auto-cycles on an interval (paused on hover/focus, disabled under
 * `prefers-reduced-motion: reduce`) so the section stays short and lively.
 */
/* Hover/focus pause behavior belongs to the composite showcase surface. */
/* oxlint-disable jsx-a11y/no-noninteractive-element-interactions */
export const FmCapabilityShowcase: React.FC<FmCapabilityShowcaseProps> = ({
  items,
  activeKey,
  defaultActiveKey,
  onActiveChange,
  autoCycleMs = 6000,
  side = 'left',
  eyebrow,
  title,
  tone = 'light',
  className,
  'data-testid': dataTestId,
}) => {
  const reactId = useId();
  const headerId = title ? `${reactId}-title` : undefined;

  const [resolvedActive, setActive] = useActiveKey({
    items,
    activeKey,
    defaultActiveKey,
    onActiveChange,
  });

  const [paused, setPaused] = useState(false);

  const select = useCallback(
    (key: string) => {
      setActive(key);
    },
    [setActive],
  );

  const advance = useCallback(() => {
    const index = items.findIndex((item) => item.key === resolvedActive);
    const next = items[(index + 1) % items.length];
    if (next) setActive(next.key);
  }, [items, resolvedActive, setActive]);

  useAutoCycle({ autoCycleMs, paused, resetToken: resolvedActive, onAdvance: advance });

  const activeItem = useMemo(
    () => items.find((item) => item.key === resolvedActive) ?? items[0],
    [items, resolvedActive],
  );

  if (items.length === 0 || !activeItem) {
    return null;
  }
  const hasMedia = hasCapabilityMedia(items);

  const rootClass = cx(
    'fm-capability-showcase',
    tone === 'dark' && 'fm-capability-showcase--dark',
    side === 'right' && 'fm-capability-showcase--media-right',
    !hasMedia && 'fm-capability-showcase--no-media',
    className,
  );

  return (
    <section
      className={rootClass}
      aria-labelledby={headerId}
      role="application"
      data-testid={dataTestId}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocusCapture={() => setPaused(true)}
      onBlurCapture={() => setPaused(false)}
    >
      {eyebrow || title ? (
        <header className="fm-capability-showcase__header">
          {eyebrow ? <span className="fm-capability-showcase__eyebrow">{eyebrow}</span> : null}
          {title ? (
            <h2 id={headerId} className="fm-capability-showcase__title">
              {title}
            </h2>
          ) : null}
        </header>
      ) : null}

      <div className="fm-capability-showcase__body">
        <CapabilityMedia
          media={activeItem.media}
          mediaIcon={activeItem.mediaIcon}
          testId={activeItem['data-testid'] ? `${activeItem['data-testid']}-media` : undefined}
        />
        <div className="fm-capability-showcase__list-col">
          <CapabilityList
            items={items}
            activeKey={resolvedActive}
            labelId={headerId}
            onSelect={select}
            autoCycleMs={autoCycleMs}
            paused={paused}
          />
        </div>
      </div>
    </section>
  );
};
/* oxlint-enable jsx-a11y/no-noninteractive-element-interactions */

export default FmCapabilityShowcase;
